import { AuthService } from './../../services/auth.service';
import { Component, AfterViewInit, OnInit, OnDestroy, ViewContainerRef, ComponentRef, ViewChild, Injectable } from '@angular/core';
import { ActivatedRoute, Router, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, Subscription } from 'rxjs/Rx';
import { Store } from '@ngrx/store';

import * as fromRoot from '../../store/reducers';
import { AppUiActions, TestTakerActions } from '../../store/actions';
import { TestNavigationService } from './../../services/test-navigation.service';
import { TestContentService } from '../../services/test-content.service';
import { TestTakerService } from '../../services/test-taker.service';
import { RepeatableRandomGeneratorService } from '../../services/repeatable-random-generator.service';
import { TestFormValidationComponent } from './test-form-validation/test-form-validation.component';
import * as Enum from '../../dtos/enums';
import { ConfigHelper } from '../../services/config.helper';
import { SignalRService } from '../../services/signalR.service';
import { MyModal } from '../common/my-modal/my-modal.component';
import { FullscreenService } from 'app/services/fullscreen.service';

@Component({
    selector: 'pg-renderer',
    template: require('./pg-renderer.component.html')
})
export class PgRendererComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('testFormValidation') testFormValidation: TestFormValidationComponent;

    testlet$: Observable<PgRendererDto.TestletDefinition>;
    itemIndex$: Observable<number>;
    assessmentItem$: Observable<PgRendererDto.AssessmentItem>;
    assessmentTest$: Observable<PgRendererDto.AssessmentTest>;
    testNavigation$: Observable<PgRendererDto.TestNavigation>;
    currentTestletId: number = null;
    assessmentTest: PgRendererDto.AssessmentTest;
    private assessmentTestSubscription: Subscription;
    private assessmentItemSubscription: Subscription;
    private testletIdSubscription: Subscription;
    private preloadTestletIdSubscription: Subscription;
    private routeParamsSubscription: Subscription;
    private testTakerInfoSubscription: Subscription;
    private endOfTestSubscription: Subscription;
    private shouldSendAssessmentTest: Boolean = true;
    private isIppMode: Boolean = false;
    private currentYear: number;

    constructor(
        private route: ActivatedRoute,
        private store: Store<fromRoot.State>,
        private repeatableRandomGeneratorService: RepeatableRandomGeneratorService,
        private testTakerService: TestTakerService,
        private testContentService: TestContentService,
        private viewContainerRef: ViewContainerRef,
        private testNavigationService: TestNavigationService,
        private signalRService: SignalRService,
        private authService: AuthService,
        private router: Router,
        private myModal: MyModal,
        private fullScreenService: FullscreenService
    ) {
        this.testlet$ = store.select(fromRoot.getTestlet);
        this.itemIndex$ = store.select(fromRoot.getItemIndex);
        this.assessmentItem$ = store.select(fromRoot.getAssessmentItem);
        this.assessmentTest$ = store.select(fromRoot.getAssessmentTest);
        this.testNavigation$ = store.select(fromRoot.getTestNavigation);
    }

    ngOnInit() {
        this.assessmentItemSubscription = this.assessmentItem$.subscribe(assessmentItem => {
            if (assessmentItem) {
                this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.SetAssessmentItemIndex, null, null, this.currentTestletId, null, assessmentItem.ItemIndex + 1);
            }
        });

        this.assessmentTestSubscription = this.assessmentTest$.subscribe(assessmentTest => {
            this.assessmentTest = assessmentTest;
            this.isIppMode = ConfigHelper.isIppMode();
            var d = new Date();
            this.currentYear = d.getFullYear();
        });

        this.testletIdSubscription = this.store.select(fromRoot.getCurrentTestletId).subscribe(testletId => {
            if (testletId) {
                if (this.currentTestletId) {
                    this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.LeaveTestlet, null, null, this.currentTestletId);
                }
                this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.EnterTestlet, null, null, testletId);
                this.currentTestletId = testletId;
                this.store.dispatch(new TestTakerActions.LoadTestlet(testletId, this.shouldSendAssessmentTest ? this.assessmentTest : null));
            }
        });

        this.preloadTestletIdSubscription = this.store.select(fromRoot.getNextTestletId).subscribe(preloadtestletId => {
            if (preloadtestletId) {
                this.store.dispatch(new TestTakerActions.PreloadTestlet(preloadtestletId, this.shouldSendAssessmentTest ? this.assessmentTest : null));
            }
        });

        this.testTakerInfoSubscription = this.store.select(fromRoot.getTestTakerInfo).subscribe(testTakerInfo => {
            if (testTakerInfo) {
                this.repeatableRandomGeneratorService.setSeed(testTakerInfo.TestTakerId);
            }
        });

        if (ConfigHelper.isTdpMode(false)) {
            const testTakerInfo = this.testTakerService.testTakerInfo;
            this.signalRService.onNewConnectionEstablished$.subscribe(x => {
                this.signalRService.onFreeze$.subscribe(testTakers => {
                    if (this.hasTestTaker(testTakers, testTakerInfo.BrandName, testTakerInfo.TestSessionSiteCode, testTakerInfo.TestTakerId)) {
                        this.store.dispatch(new AppUiActions.ShowPauseScreen());
                    }
                });
                this.signalRService.onUnFreeze$.subscribe(testTakers => {
                    if (this.hasTestTaker(testTakers, testTakerInfo.BrandName, testTakerInfo.TestSessionSiteCode, testTakerInfo.TestTakerId)) {
                        this.store.dispatch(new AppUiActions.HidePauseScreen());
                        this.store.dispatch(new TestTakerActions.ResumeTest());
                        this.store.dispatch(new TestTakerActions.LoadTestlet(this.currentTestletId, this.shouldSendAssessmentTest ? this.assessmentTest : null));
                    }
                });
            });
            this.signalRService.initializeConnection();
        }
    }

    private hasTestTaker(testTakers: TestTakerHubDto[], brandName: string, siteCode: string, testTakerId: number) {
        return testTakers.findIndex(tt => tt.BrandName == brandName && tt.SiteCode == siteCode && tt.TestTakerId == testTakerId) >= 0;
    }

    ngAfterViewInit() {
        if (ConfigHelper.isTdpMode(true)) {
            const accessToken = this.authService.getAccessToken();
            if (!accessToken) {
                const webDeliveryAuth = this.authService.getWebDeliveryAuth();
                if (webDeliveryAuth) {
                    this.router.navigateByUrl('/start-screen-web/' + encodeURIComponent(webDeliveryAuth));
                    return;
                }

                this.myModal.showRegularDlg("Error", "Information missing. Please restart the web test from the 'My Account' page.");
            } else {
                this.testNavigationService.enableWindowUnloadingWarning();

                window.history.pushState(null, "", window.location.href);
                window.onpopstate = function() {
                    window.history.pushState(null, "", window.location.href);
                };

                this.endOfTestSubscription = this.testNavigation$.subscribe(testNavigation => {
                    if (testNavigation.isEndOfTest) {
                        this.fullScreenService.tryExitFullScreen();
                        this.endOfTestSubscription.unsubscribe();
                    }
                });
            }
        }

        this.routeParamsSubscription = this.route.params.subscribe(params => {
            const testTypeId = this.route.snapshot.queryParams['test-type'];
            if (testTypeId) {
                ConfigHelper.setTestTypeId(testTypeId);
            }
            if (params) {
                if (params['id']) {
                    document.title = "Preview Testlet";
                    this.store.dispatch(new TestTakerActions.LoadTestlet(params['id'], null));
                } else if (params['snapshot-id']) {
                    document.title = "Preview Snapshot";
                    this.store.dispatch(new TestTakerActions.LoadSnapshotTestlet(params['snapshot-id']));
                } else if (params['test-form-id']) {
                    document.title = "Preview Test Form";
                    this.store.dispatch(new TestTakerActions.LoadAssessmentTestByTestForm(params['test-form-id']));
                } else if (params['bulk-test-form-ids']) {
                    document.title = "Preview Test Forms";
                    if (params['validate']) {
                        this.testFormValidation.show(this.viewContainerRef, params['bulk-test-form-ids']);
                    } else {
                        this.store.dispatch(new TestTakerActions.LoadAssessmentTestByTestForm(params['bulk-test-form-ids']));
                    }
                } else if (params['assessment-test-id']) {
                    document.title = "Assessment Test";
                    const assessmentTestId = params['assessment-test-id'];
                    this.store.dispatch(new TestTakerActions.LoadAssessmentTest(assessmentTestId));
                } else if (params['testlet-version-id']) {
                    document.title = "Preview Testlet Version";
                    this.store.dispatch(new TestTakerActions.LoadTestletVersion(params['testlet-version-id']));
                } else if (params['ipp-assessment-test-id']) {
                    document.title = "IPP Test Form";
                    this.store.dispatch(new TestTakerActions.LoadIppAssessmentTest(params['ipp-assessment-test-id']));
                } else if (params['ipp-assessment-test-id-for-latest']) {
                    document.title = "IPP Test Form";
                    this.shouldSendAssessmentTest = false;
                    this.store.dispatch(new TestTakerActions.LoadLatestIppAssessmentTest(params['ipp-assessment-test-id-for-latest']));
                } else if (params['ipp-product-id']) {
                    document.title = "Instructional Products";
                    this.store.dispatch(new TestTakerActions.LoadIppProduct(params['ipp-product-id']));
                }
            }
        });
    }

    ngOnDestroy() {
        if (this.assessmentItemSubscription) {
            this.assessmentItemSubscription.unsubscribe();
        }
        if (this.testletIdSubscription) {
            this.testletIdSubscription.unsubscribe();
        }
        if (this.preloadTestletIdSubscription) {
            this.preloadTestletIdSubscription.unsubscribe();
        }
        if (this.routeParamsSubscription) {
            this.routeParamsSubscription.unsubscribe();
        }

        if (this.endOfTestSubscription) {
            this.endOfTestSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/components/pg-renderer/pg-renderer.component.ts